import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import LatexRenderer from "./LatexRenderer";
import { getFlashcardParts } from "../utils/flashcardText";
import { FSRS_LABELS, FSRS_RATING } from "../utils/fsrs";
import formatTime from "../utils/formatTime";

const renderMathText = (text, keyPrefix) => {
  const parts = getFlashcardParts(text || "");
  return parts.map((part, index) => {
    if (part.type === "latex") {
      const className = part.displayMode
        ? "flashcard-math flashcard-math--block"
        : "flashcard-math flashcard-math--inline";
      const Wrapper = part.displayMode ? "div" : "span";
      return (
        <Wrapper key={`${keyPrefix}-latex-${index}`} className={className}>
          <LatexRenderer latex={part.value} displayMode={part.displayMode} />
        </Wrapper>
      );
    }
    return <span key={`${keyPrefix}-text-${index}`}>{part.value}</span>;
  });
};

const normalizeFlashcardFrontText = (value) => {
  let text = String(value || "");
  text = text.replace(
    /^Method choice:\s*/i,
    "Pick the method for this problem:\n"
  );
  text = text.replace(
    /^Trigger\s*\/?\s*cloze:\s*/i,
    "What clue tells you the method?\n"
  );
  text = text.replace(
    /^Common trap:\s*/i,
    "Common trap to avoid:\n"
  );
  return text;
};

const FlashcardWarmup = ({
  cards = [],
  maxDurationMs = 180000,
  onRate,
  onComplete,
  onOpenLibrary,
  title = "Warmup",
  showTimer = true,
  exitLabel = "Skip",
}) => {
  const [index, setIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remainingSec, setRemainingSec] = useState(
    showTimer && Number.isFinite(maxDurationMs)
      ? Math.round(maxDurationMs / 1000)
      : null
  );
  const [flipHeight, setFlipHeight] = useState(160);
  const frontFaceRef = useRef(null);
  const backFaceRef = useRef(null);

  const activeCard = cards[index] || null;
  const cardCount = cards.length;
  const frontLabel = showBack ? "Answer" : "Prompt";
  const normalizedFront = normalizeFlashcardFrontText(activeCard?.front || "");
  const normalizedBack = activeCard?.back || "";

  useEffect(() => {
    setIndex(0);
    setShowBack(false);
    setBusy(false);
    setRemainingSec(
      showTimer && Number.isFinite(maxDurationMs)
        ? Math.round(maxDurationMs / 1000)
        : null
    );
  }, [cards, maxDurationMs, showTimer]);

  useEffect(() => {
    if (!showTimer || !Number.isFinite(maxDurationMs)) return undefined;
    if (!cardCount) return undefined;
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const remaining = Math.max(0, Math.round(maxDurationMs / 1000) - elapsed);
      setRemainingSec(remaining);
      if (remaining <= 0) {
        onComplete?.("timeout");
      }
    };
    const interval = setInterval(tick, 1000);
    tick();
    return () => {
      clearInterval(interval);
    };
  }, [cardCount, maxDurationMs, onComplete, showTimer]);

  const measureFlipHeight = () => {
    const frontHeight = frontFaceRef.current?.scrollHeight || 0;
    const backHeight = backFaceRef.current?.scrollHeight || 0;
    const next = Math.max(frontHeight, backHeight, 160);
    setFlipHeight(next);
  };

  useLayoutEffect(() => {
    if (!activeCard) return;
    const raf = requestAnimationFrame(() => measureFlipHeight());
    return () => cancelAnimationFrame(raf);
  }, [activeCard, normalizedFront, normalizedBack, showBack]);

  useEffect(() => {
    if (!activeCard) return undefined;
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => measureFlipHeight());
    if (frontFaceRef.current) observer.observe(frontFaceRef.current);
    if (backFaceRef.current) observer.observe(backFaceRef.current);
    return () => observer.disconnect();
  }, [activeCard]);

  const handleRate = async (rating) => {
    if (!activeCard || !onRate || busy) return;
    setBusy(true);
    try {
      await onRate(activeCard, rating);
      const nextIndex = index + 1;
      if (nextIndex >= cardCount) {
        onComplete?.("done");
      } else {
        setIndex(nextIndex);
        setShowBack(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const ratingButtons = useMemo(
    () => [
      { rating: FSRS_RATING.AGAIN, label: FSRS_LABELS[FSRS_RATING.AGAIN] },
      { rating: FSRS_RATING.HARD, label: FSRS_LABELS[FSRS_RATING.HARD] },
      { rating: FSRS_RATING.GOOD, label: FSRS_LABELS[FSRS_RATING.GOOD] },
      { rating: FSRS_RATING.EASY, label: FSRS_LABELS[FSRS_RATING.EASY] },
    ],
    []
  );

  if (!activeCard) return null;

  return (
    <div className="flashcard-warmup">
      <div className="flashcard-warmup__header">
        <div>
          <p style={{ margin: 0, fontWeight: 700 }}>{title}</p>
          <p className="mini-label" style={{ margin: 0 }}>
            Card {index + 1} of {cardCount}
            {showTimer && typeof remainingSec === "number"
              ? ` · ${formatTime(remainingSec)} left`
              : ""}
          </p>
        </div>
        <div className="cluster" style={{ gap: "8px" }}>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => {
              if (typeof onOpenLibrary === "function") {
                onOpenLibrary();
                return;
              }
              window.location.assign("/Flashcards");
            }}
            disabled={busy}
          >
            Flashcards
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => onComplete?.("skip")}
            disabled={busy}
          >
            {exitLabel}
          </button>
        </div>
      </div>

      <div className="flashcard-warmup__card">
        <p className="mini-label" style={{ margin: 0 }}>
          {frontLabel}
        </p>
        <div
          className={`flashcard-warmup__flip${
            showBack ? " flashcard-warmup__flip--flipped" : ""
          }`}
          style={{ height: `${flipHeight}px` }}
          onClick={() => setShowBack((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setShowBack((prev) => !prev);
            }
          }}
        >
          <div
            ref={frontFaceRef}
            className="flashcard-warmup__face flashcard-warmup__face--front"
          >
            {renderMathText(normalizedFront, `front-${activeCard.id}`)}
          </div>
          <div
            ref={backFaceRef}
            className="flashcard-warmup__face flashcard-warmup__face--back"
          >
            <div className="flashcard-warmup__answer">
              {renderMathText(normalizedBack, `back-${activeCard.id}`)}
            </div>
          </div>
        </div>
        {!showBack ? (
          <div className="flashcard-warmup__actions">
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={() => setShowBack(true)}
              disabled={busy}
            >
              Show answer
            </button>
          </div>
        ) : (
          <div className="flashcard-warmup__actions">
            {ratingButtons.map((button) => (
              <button
                key={button.rating}
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => handleRate(button.rating)}
                disabled={busy}
              >
                {button.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardWarmup;
